/**
 * Checkout API Service
 *
 * Handles all checkout-related API calls
 * Following WooCommerce-style API patterns
 *
 * @package Enterprise\BookingSystem\Services
 */

/**
 * Internal dependencies
 */
import { BaseApiService } from "./base-api.service";
import type {
	BillingAddress,
	ShippingAddress,
	CheckoutApiResponse,
	UpdateCartAddressResponse,
} from "../../data/checkout/types";

/**
 * Update cart billing address command
 */
export type UpdateBillingAddressCommand = {
	billing: BillingAddress;
	cart_id?: number;
};

/**
 * Update cart shipping address command
 */
export type UpdateShippingAddressCommand = {
	cart_id?: number;
	ship_to_billing?: boolean;
	shipping: ShippingAddress;
};

/**
 * Payment Intent Response
 */
export type PaymentIntentResponse = {
	amount: number;
	clientSecret: string;
	currency: string;
	paymentIntentId: string;
	publishableKey: string;
};

/**
 * Process checkout command
 */
export type ProcessCheckoutCommand = {
	cart_id?: number;
	// Customer info (from billing address)
	email: string;
	first_name: string;
	last_name: string;
	phone: string;
	// Billing address
	billing: BillingAddress;
	// Shipping address
	ship_to_billing: boolean;
	shipping: ShippingAddress;
	// Payment
	payment_method: string;
	paymentIntentId?: string;
	// Optional
	customer_notes?: string;
	marketing_opt_in: boolean;
};

/**
 * Checkout API Service Class
 */
class CheckoutApiService extends BaseApiService {
	/**
	 * Update cart billing address
	 *
	 * @param command Update billing address command
	 * @returns Promise with API response
	 */
	async updateBillingAddress(
		command: UpdateBillingAddressCommand,
	): Promise<UpdateCartAddressResponse> {
		return this.post<UpdateCartAddressResponse>(
			"/checkout/billing-address",
			command,
		);
	}

	/**
	 * Update cart shipping address
	 *
	 * @param command Update shipping address command
	 * @returns Promise with API response
	 */
	async updateShippingAddress(
		command: UpdateShippingAddressCommand,
	): Promise<UpdateCartAddressResponse> {
		return this.post<UpdateCartAddressResponse>(
			"/checkout/shipping-address",
			command,
		);
	}

	/**
	 * Update both billing and shipping addresses
	 *
	 * @param billing Billing address
	 * @param shipping Shipping address
	 * @param shipToBilling Whether shipping same as billing
	 * @returns Promise with API response
	 */
	async updateAddresses(
		billing: BillingAddress,
		shipping: ShippingAddress,
		shipToBilling = false,
	): Promise<UpdateCartAddressResponse> {
		return this.post<UpdateCartAddressResponse>("/checkout/addresses", {
			billing,
			shipping: shipToBilling ? billing : shipping,
			ship_to_billing: shipToBilling,
		});
	}

	/**
	 * Create Stripe Payment Intent for the current cart
	 *
	 * @returns Promise with Payment Intent details
	 */
	async createPaymentIntent(): Promise<PaymentIntentResponse> {
		return this.post<PaymentIntentResponse>(
			"/checkout/create-payment-intent",
			{},
		);
	}

	/**
	 * Process checkout and create order
	 *
	 * @param command Checkout command
	 * @returns Promise with checkout response
	 */
	async processCheckout(
		command: ProcessCheckoutCommand,
	): Promise<CheckoutApiResponse> {
		return this.post<CheckoutApiResponse>("/checkout/process", command);
	}

	/**
	 * Validate checkout data without processing
	 *
	 * @param command Checkout command
	 * @returns Promise with validation response
	 */
	async validateCheckout(
		command: Partial<ProcessCheckoutCommand>,
	): Promise<{ errors?: Record<string, string>; success: boolean }> {
		return this.post("/checkout/validate", command);
	}
}

export const checkoutApiService = new CheckoutApiService();
